/**
 * Encryption utilities using Web Crypto API for localStorage data
 *
 * This module provides AES-256-GCM encryption with PBKDF2 key derivation
 * to secure data stored in localStorage.
 */

const APP_SALT = 'time-tracker-v1';
const PBKDF2_ITERATIONS = 100000;
const ENC_PREFIX = 'enc:';

// Cache for derived keys to improve performance
const keyCache = new Map();

/**
 * Checks if the Web Crypto API is available
 * @returns {boolean} True if Web Crypto API is available
 */
export function isCryptoAvailable() {
  return typeof window !== 'undefined' &&
         window.crypto &&
         window.crypto.subtle;
}

/**
 * Derives an encryption key from a user UID using PBKDF2
 * @param {string} uid - User's Firebase UID
 * @returns {Promise<CryptoKey>} Derived encryption key
 */
async function deriveKeyFromUid(uid) {
  if (!isCryptoAvailable()) {
    throw new Error('Web Crypto API is not available');
  }

  // Check cache first
  if (keyCache.has(uid)) {
    return keyCache.get(uid);
  }

  try {
    // Create salt from uid + app constant
    const saltString = `${APP_SALT}:${uid}`;
    const salt = new TextEncoder().encode(saltString);

    // Import the uid as key material
    const keyMaterial = await window.crypto.subtle.importKey(
      'raw',
      new TextEncoder().encode(uid),
      'PBKDF2',
      false,
      ['deriveBits', 'deriveKey']
    );

    // Derive AES-256-GCM key
    const key = await window.crypto.subtle.deriveKey(
      {
        name: 'PBKDF2',
        salt: salt,
        iterations: PBKDF2_ITERATIONS,
        hash: 'SHA-256'
      },
      keyMaterial,
      { name: 'AES-GCM', length: 256 },
      false,
      ['encrypt', 'decrypt']
    );

    // Cache the key
    keyCache.set(uid, key);

    return key;
  } catch (error) {
    console.error('Error deriving encryption key:', error);
    throw error;
  }
}

/**
 * Converts ArrayBuffer to Base64 string
 * @param {ArrayBuffer} buffer - Buffer to convert
 * @returns {string} Base64 encoded string
 */
function bufferToBase64(buffer) {
  const bytes = new Uint8Array(buffer);
  let binary = '';
  for (let i = 0; i < bytes.byteLength; i++) {
    binary += String.fromCharCode(bytes[i]);
  }
  return btoa(binary);
}

/**
 * Converts Base64 string to ArrayBuffer
 * @param {string} base64 - Base64 encoded string
 * @returns {ArrayBuffer} Decoded buffer
 */
function base64ToBuffer(base64) {
  const binary = atob(base64);
  const bytes = new Uint8Array(binary.length);
  for (let i = 0; i < binary.length; i++) {
    bytes[i] = binary.charCodeAt(i);
  }
  return bytes.buffer;
}

/**
 * Checks if data is encrypted (starts with 'enc:')
 * @param {string} data - Data to check
 * @returns {boolean} True if data appears to be encrypted
 */
export function isEncrypted(data) {
  return typeof data === 'string' && data.startsWith(ENC_PREFIX);
}

/**
 * Encrypts data using AES-256-GCM
 * @param {any} data - Data to encrypt (will be JSON stringified)
 * @param {string} uid - User's Firebase UID for key derivation
 * @returns {Promise<string>} Base64 encoded encrypted data with IV and prefix
 */
export async function encryptData(data, uid) {
  if (!uid) {
    throw new Error('UID is required for encryption');
  }

  if (!isCryptoAvailable()) {
    console.warn('Web Crypto API not available, storing data unencrypted');
    return JSON.stringify(data);
  }

  try {
    // Get or derive encryption key
    const key = await deriveKeyFromUid(uid);

    // Generate random IV (12 bytes for GCM)
    const iv = window.crypto.getRandomValues(new Uint8Array(12));

    // Convert data to JSON and encode as UTF-8
    const jsonString = JSON.stringify(data);
    const encodedData = new TextEncoder().encode(jsonString);

    // Encrypt
    const encryptedBuffer = await window.crypto.subtle.encrypt(
      {
        name: 'AES-GCM',
        iv: iv
      },
      key,
      encodedData
    );

    // Combine IV + encrypted data
    const combined = new Uint8Array(iv.length + encryptedBuffer.byteLength);
    combined.set(iv, 0);
    combined.set(new Uint8Array(encryptedBuffer), iv.length);

    // Convert to base64 and add prefix
    const base64 = bufferToBase64(combined.buffer);
    return `${ENC_PREFIX}${base64}`;
  } catch (error) {
    console.error('Encryption failed:', error);
    // Fallback to unencrypted storage
    console.warn('Falling back to unencrypted storage');
    return JSON.stringify(data);
  }
}

/**
 * Decrypts data using AES-256-GCM
 * @param {string} encryptedData - Base64 encoded encrypted data with prefix
 * @param {string} uid - User's Firebase UID for key derivation
 * @returns {Promise<any>} Decrypted and parsed data, or null if decryption fails
 */
export async function decryptData(encryptedData, uid) {
  if (!uid) {
    console.error('UID is required for decryption');
    return null;
  }

  if (!isCryptoAvailable()) {
    console.warn('Web Crypto API not available');
    return null;
  }

  try {
    // Remove prefix
    if (!encryptedData.startsWith(ENC_PREFIX)) {
      console.error('Invalid encrypted data format (missing prefix)');
      return null;
    }

    const base64Data = encryptedData.substring(ENC_PREFIX.length);

    // Convert from base64
    const combined = new Uint8Array(base64ToBuffer(base64Data));

    // Extract IV (first 12 bytes) and encrypted data
    const iv = combined.slice(0, 12);
    const encryptedBytes = combined.slice(12);

    // Get or derive decryption key
    const key = await deriveKeyFromUid(uid);

    // Decrypt
    const decryptedBuffer = await window.crypto.subtle.decrypt(
      {
        name: 'AES-GCM',
        iv: iv
      },
      key,
      encryptedBytes
    );

    // Convert to string and parse JSON
    const jsonString = new TextDecoder().decode(decryptedBuffer);
    return JSON.parse(jsonString);
  } catch (error) {
    console.error('Decryption failed:', error);
    return null;
  }
}

/**
 * Clears the key cache (useful on logout)
 */
export function clearKeyCache() {
  keyCache.clear();
}
